import { useMemo } from 'react';
import { SERVICE_CATALOG, CATEGORY_META, serviceFormHref } from '../mock';
import { useDocumentHead, SITE_URL } from './useDocumentHead';

function truncate(text, max = 155) {
  if (!text || text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : max)}…`;
}

function buildFaqSchema(faqs) {
  if (!faqs || faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

function buildBreadcrumbSchema(categoryLabel, categoryPath, title, servicePath) {
  const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` }];
  if (categoryLabel) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: categoryLabel,
      item: `${SITE_URL}${categoryPath}`,
    });
  }
  items.push({
    '@type': 'ListItem',
    position: items.length + 1,
    name: title,
    item: `${SITE_URL}${servicePath}`,
  });
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
}

/**
 * Sets per-service page title/description/canonical and injects FAQPage +
 * BreadcrumbList JSON-LD built from the same content already shown on the
 * page — this is what lets Google's AI Overview and answer engines (ChatGPT,
 * Gemini, Perplexity) surface SolveTax directly for a service's FAQs.
 */
export function useServiceSEO({ code, title, content }) {
  const { jsonLd, seoTitle, seoDescription, path } = useMemo(() => {
    const serviceMeta = SERVICE_CATALOG.find((s) => s.code === code);
    const categoryLabel = serviceMeta ? CATEGORY_META[serviceMeta.category]?.label : null;
    const categoryPath = serviceMeta ? `/all-services?cat=${serviceMeta.category}` : '/all-services';
    const servicePath = code ? serviceFormHref(code) : '/';

    return {
      path: servicePath,
      seoTitle: title ? `${title} | SolveTax` : undefined,
      seoDescription: truncate(content?.intro?.[0]),
      jsonLd: {
        faq: buildFaqSchema(content?.faqs),
        breadcrumb: title ? buildBreadcrumbSchema(categoryLabel, categoryPath, title, servicePath) : null,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, title, content]);

  useDocumentHead({ title: seoTitle, description: seoDescription, path, jsonLd });
}
